import type { AppConfig } from "../../types/appConfig";
import type {
  ZekayCustomer,
  CustomersPaginationRequest,
  SearchCustomersRequest,
} from "./Customer.typed";
import type { Excursion } from "../excursions/Excursion.typed";
import type { PaginatedResponse } from "../zekay/pagination";
import { assertResponseOk, expectStatuses, fetchZekay } from "../zekay/httpClient";

export class CustomerModel {
  zekayAPI: string;

  constructor(config: AppConfig) {
    this.zekayAPI = config.zekayAPI;
  }

  async getCustomers(
    paginationParams?: CustomersPaginationRequest,
  ): Promise<PaginatedResponse<ZekayCustomer> | ZekayCustomer[]> {
    const params = new URLSearchParams();
    if (paginationParams) {
      if (paginationParams.page !== undefined)
        params.append("page", paginationParams.page.toString());
      if (paginationParams.limit !== undefined)
        params.append("limit", paginationParams.limit.toString());
      if (paginationParams.sort_order) params.append("sort_order", paginationParams.sort_order);
      if (paginationParams.q && paginationParams.q.trim().length >= 2) {
        params.append("q", paginationParams.q.trim());
      }
      if (paginationParams.qualification && paginationParams.qualification.trim() !== "") {
        params.append("qualification", paginationParams.qualification.trim());
      }
    }

    const url = `${this.zekayAPI}/customers${params.toString() ? "?" + params.toString() : ""}`;

    const response = await fetchZekay(url);
    await expectStatuses(response, 200, 204);
    if (response.status === 204) {
      return paginationParams ? { data: [], total: 0, page: 1, limit: 50, total_pages: 0 } : [];
    }
    const result = await response.json();
    if (
      paginationParams &&
      (paginationParams.page !== undefined || paginationParams.limit !== undefined)
    ) {
      return result as PaginatedResponse<ZekayCustomer>;
    }
    return Array.isArray(result) ? result : result.data || [];
  }

  async getCustomer(id: string): Promise<ZekayCustomer | undefined> {
    const response = await fetchZekay(this.zekayAPI + "/customers/" + id, {
      cache: "no-store",
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
    });
    if (response.status === 404) return undefined;
    await assertResponseOk(response);
    return (await response.json()) as ZekayCustomer;
  }

  async createCustomer(payload: ZekayCustomer): Promise<ZekayCustomer> {
    const res = await fetchZekay(this.zekayAPI + "/customers", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await expectStatuses(res, 201, 200);
    return (await res.json()) as ZekayCustomer;
  }

  async deleteCustomer(id: number): Promise<void> {
    const res = await fetchZekay(`${this.zekayAPI}/customers/${id}`, { method: "DELETE" });
    await assertResponseOk(res);
  }

  async updateCustomer(id: number, payload: ZekayCustomer): Promise<ZekayCustomer> {
    const res = await fetchZekay(`${this.zekayAPI}/customers/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await expectStatuses(res, 200, 204);
    if (res.status === 204) return payload;
    return (await res.json()) as ZekayCustomer;
  }

  async updateFile(formData: FormData): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + "/customers/file", {
      method: "POST",
      body: formData,
    });
    await expectStatuses(response, 204);
  }

  async deleteCustomerFile(payload: {
    CustomerID: number;
    Type: "photo" | "medical";
  }): Promise<void> {
    const response = await fetchZekay(this.zekayAPI + "/customers/file", {
      method: "DELETE",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(payload),
    });
    await expectStatuses(response, 204);
  }

  private static readonly BATCH_MAX_IDS = 100;

  async getCustomersBatch(ids: number[]): Promise<ZekayCustomer[]> {
    if (ids.length === 0) return [];
    const unique = Array.from(new Set(ids));
    const chunks: number[][] = [];
    for (let i = 0; i < unique.length; i += CustomerModel.BATCH_MAX_IDS) {
      chunks.push(unique.slice(i, i + CustomerModel.BATCH_MAX_IDS));
    }
    const results = await Promise.all(
      chunks.map(async (chunk) => {
        const response = await fetchZekay(this.zekayAPI + "/customers/batch", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ ids: chunk }),
        });
        await expectStatuses(response, 200, 204);
        if (response.status === 204) return [];
        return (await response.json()) as ZekayCustomer[];
      }),
    );
    return results.flat();
  }

  async searchCustomers(searchRequest: SearchCustomersRequest): Promise<ZekayCustomer[]> {
    const response = await fetchZekay(this.zekayAPI + "/customers/search", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(searchRequest),
    });
    await expectStatuses(response, 200, 204);
    if (response.status === 204) return [];
    return (await response.json()) as ZekayCustomer[];
  }

  async getCustomerExcursions(
    customerId: number,
    page = 1,
    limit = 10,
  ): Promise<PaginatedResponse<Excursion>> {
    const params = new URLSearchParams({
      page: page.toString(),
      limit: limit.toString(),
    });
    const response = await fetchZekay(
      `${this.zekayAPI}/customers/${customerId}/excursions?${params}`,
    );
    await expectStatuses(response, 200, 204);

    if (response.status === 204) {
      return { data: [], total: 0, page: 1, limit, total_pages: 0 };
    }

    return (await response.json()) as PaginatedResponse<Excursion>;
  }
}
